import { CommonModule, DatePipe } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Api } from '../../services/api';
import { ChatHistorySupervisor } from '../chat-history-supervisor/chat-history-supervisor';
import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { AuditLogRiskOfficer } from '../audit-log-risk-officer/audit-log-risk-officer';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { Auth } from '../../services/auth';
import { LoaderService } from '../../services/loader';
import { Toaster } from '../../services/toaster';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { fadeInAnimation } from '../../animation';
import { trigger, state, style, animate, transition } from '@angular/animations';

interface Issue {
  IssueId: number;
  AssociateId: number;
  AssociateName: string;
  ProjId: number;
  ProcessId: number;
  PFLMId: number;
  CreatedDate: Date;
  IssueStatus: string;
  Frequency: string;
  IsUpdated: boolean;
  ConversationDate: string;
  ConversationId: string;
}


@Component({
  selector: 'app-issuelistsr',
  imports: [CommonModule, FormsModule, ChatHistorySupervisor, BsDatepickerModule, BsDatepickerModule, FormsModule, ReactiveFormsModule, AuditLogRiskOfficer, TooltipModule],
  templateUrl: './issuelistsr.html',
  providers: [DatePipe],
  styleUrl: './issuelistsr.scss',
  animations: [fadeInAnimation, trigger('datepickerAnimation', [state('open', style({ opacity: 1 })), state('closed', style({ opacity: 0 })), transition('open => closed', [animate('0.5s')]), transition('closed => open', [animate('0.5s')])])],

})
export class Issuelistsr implements OnInit, OnDestroy {
  // Default properties
  showContent = true;
  botProfilePic = 'assets/images/chatbotmsgicon.svg';
  dotPic = 'assets/images/dot.svg';
  IssueListSRDefaultDateTime = new Date().toLocaleString();
  datePickerConfig: Partial<BsDatepickerConfig>;

  currentDate: any = null;
  currentProject: number = 0;
  currentProcess: number = 0;
  currentUser: number = 0;

  dateRange: Date[] | undefined;
  dateRangeControl = new FormControl<Date[] | null>(null);

  projectList: any[] = [];
  processList: any[] = [];
  associatedList: any[] = []; // Originals for reset (NEW) o
  originalProcessList: any[] = [];
  originalAssociatedList: any[] = [];
  originalData: Issue[] = [];

  issues: Issue[][] = [];
  userIssues: Issue[] = [];
  private subscription: Subscription | null = null;
  reviewerID = 0;
  showChatHistory = false;
  showAuditLog = false;
  chatIssueIds: string = '';
  auditLogIssueIds: string = '';
  auditLogPflmId: number = 0;
  userDetails: any = {};
  role: string = '';
  private destroy$ = new Subject<void>();
  selectedIssueId: number | null = null;
  selectedConversationId: string = '';
  selectedPFLMIdChatHistory: number | null = 0;
  statusData: any[] = [];
  allStatusData: any[] = []; // unfiltered — used for audit log display
  reasonsData: any[] = [];
  impactAreaData: any[] = [];
  severityData: any[] = [];

  @ViewChild('chatHistorySection', { read: ElementRef }) chatHistorySection?: ElementRef;
  @ViewChild('auditLogSection', { read: ElementRef }) auditLogSection?: ElementRef;

  constructor(private datePipe: DatePipe, private apiService: Api,
    private authService: Auth, private loaderService: LoaderService,
    private toasterService: Toaster) {
    this.datePickerConfig = {
      containerClass: 'theme-dark-blue',
      rangeInputFormat: 'MM/DD/YYYY',
      showWeekNumbers: false,
      customTodayClass: 'custom-today'
    };
    this.filterIssuesByFrequecy('Daily', 'default');
  }

  ngOnInit(): void {
    this.userDetails = this.authService.getUserDetails();
    this.role = this.userDetails.role || '';

    this.reviewerID = this.userDetails.eid || 0;
    this.fetchMasterData();
    this.inItComponent();

    this.apiService.refresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.fetchIssuesTableData(this.reviewerID);
      });

    this.apiService.showChatHistory$
      .pipe(takeUntil(this.destroy$))
      .subscribe(show => {
        const wasVisible = this.showChatHistory;
        this.showChatHistory = show;
        // Repeat click while already visible: data is already there, scroll on next frame
        if (show && wasVisible) {
          requestAnimationFrame(() => this.scrollToChatHistory());
        }
      });

    this.apiService.chatHistoryIssueIds$
      .pipe(takeUntil(this.destroy$))
      .subscribe(ids => this.chatIssueIds = ids);

    this.apiService.showAuditLog$
      .pipe(takeUntil(this.destroy$))
      .subscribe(show => {
        const wasVisible = this.showAuditLog;
        this.showAuditLog = show;
        if (show && wasVisible) {
          requestAnimationFrame(() => this.scrollToAuditLog());
        }
      });

    this.apiService.auditLogIssueIds$
      .pipe(takeUntil(this.destroy$))
      .subscribe(ids => this.auditLogIssueIds = ids);

    this.apiService.auditLogPflmId$
      .pipe(takeUntil(this.destroy$))
      .subscribe(id => this.auditLogPflmId = Number(id || 0));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onChatHistoryDataLoaded(): void {
    setTimeout(() => requestAnimationFrame(() => this.scrollToChatHistory()), 0);
  }

  onAuditLogDataLoaded(): void {
    setTimeout(() => requestAnimationFrame(() => this.scrollToAuditLog()), 0);
  }

  private scrollToChatHistory(): void {
    this.chatHistorySection?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private scrollToAuditLog(): void {
    this.auditLogSection?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private fetchMasterData(): void {
    this.apiService.GetMasterDataForDropdown().subscribe({
      next: (res: any) => {
        this.severityData = res?.severities || [];
        this.impactAreaData = res?.impact_areas || [];

        this.allStatusData = res?.statuses ?? [];
        const allowedStatusIds = new Set([2, 3]);
        this.statusData = this.allStatusData.filter(
          (st: any) => allowedStatusIds.has(st?.issue_status_id)
        );

        const reasons = res?.reasons ?? res?.Reasons ?? [];
        this.reasonsData = reasons.map((r: any) => ({
          id: r?.id ?? null,
          reason: r?.reason ?? ''
        }));
      },
      error: (err: any) => {
        console.error('[IssueListSR] Master data API error:', err);
      }
    });
  }

  private inItComponent(): void {
    this.showContent = false; // For now, hardcode associateId 
    const associateId = this.reviewerID;
    this.fetchIssuesTableData(associateId);
    setTimeout(() => { this.showContent = true; }, 0);
  }

private getIssuesApi(payload: any) {
  if (this.isRiskOfficer) {
    return this.apiService.GetBUListByRiskOfficer(payload);
  }

  // Covers 'supervisor', 'supervisors', and acts as the default fallback
  return this.apiService.GetFLMListBySupervisor(payload);
}

  fetchIssuesTableData(associateId: number): void {
    this.loaderService.show();
    const payload = { AssociateId: `${associateId}` };
    this.issues = [];
    this.associatedList = [];

    // this.apiService.GetFLMListBySupervisor(payload).subscribe({
    this.getIssuesApi(payload).subscribe({
      next: (response: any) => {
        this.loaderService.hide();
        // Map backend response into frontend arrays

        const resp = this.normalizeResponse(response);
        this.projectList = resp?.Table?.map((p: any) => ({
          ProjId: p.ProjId,
          ProjectName: p.ProjectName
        })) ?? [];

        // FIX: include ProjId when mapping processes 
        this.originalProcessList = resp?.Table1?.map((pr: any) => ({
          ProcessId: pr.ProcessId,
          ProcessLocationName: pr.ProcessLocationName,
          ProjId: pr.ProjId // <-- important! 
        })) ?? [];
        // Start with all processes available 

        this.originalData = resp?.Table2?.map((item: any) => ({
          IssueId: item.IssueId,
          AssociateId: item.AssociateId,
          AssociateName: item.AssociateName,
          ProjId: item.ProjId,
          ProcessId: item.ProcessId,
          PFLMId: item.PFLMId,
          CreatedDate: new Date(item.CreatedDate),
          IssueStatus: item.IssueStatus,
          Frequency: 'Daily', // default since backend doesn’t provide
          IsUpdated: item.is_updated,
          ConversationDate: item.ConversationDate,
          ConversationId: item.ConversationId ?? ''
        })) ?? [];

        // ✅ Map associates from Table2 (critical step you missed) 
        this.originalAssociatedList = resp?.Table2?.map((a: any) => ({
          AssociateId: +a.AssociateId,
          AssociateName: a.AssociateName,
          ProcessId: a.ProcessId
        })) ?? [];
        // ✅ Deduplicate associates by AssociateId + ProcessId this.originalAssociatedList = this.originalAssociatedList.filter( (value, index, self) => index === self.findIndex((t) => t.AssociateId === value.AssociateId && t.ProcessId === value.ProcessId ) );

        // Optionally run default filter
        this.filterIssuesByFrequecy('Daily', 'default');
      },
      error: (e: any) => {
        this.loaderService.hide();
        console.error('Error fetching issues', e);
      }
    });
  }

  private normalizeResponse(resp: any) {
    // If new RiskOfficer format → convert it
    if (resp?.projects && resp?.processes && resp?.issues) {
      return {
        Table: resp.projects.map((p: any) => ({
          ProjId: p.proj_id,
          ProjectId: p.project_id,
          ProjectName: p.project_name
        })),

        Table1: resp.processes.map((p: any) => ({
          ProcessId: p.process_id,
          ProcessLocationName: p.process_location_name,
          ProjId: p.proj_id
        })),

        Table2: resp.issues.map((i: any) => ({
          FLMId: i.flm_id,
          AssociateId: i.associate_id,
          AssociateName: i.associate_name,
          PFLMId: i.pflm_id,
          IssueId: i.issue_id,
          IssueStatus: i.issue_status,
          CreatedDate: i.created_date,
          ConversationDate: i.conversation_date,
          ConversationId: i.conversation_id ?? '',
          ProcessId: i.process_id,
          ProjId: i.proj_id
        }))
      };
    }

    // Otherwise, it's the Supervisor response already
    return resp;
  }

  onClickUser(index: number) {
    console.log('Accordion user clicked:', index);
  }

  // shouldShowDot(issue: Issue): boolean {
  //   return this.role === 'Supervisor' && issue.IsUpdated === true;
  // }

  shouldShowDot(issue: Issue): boolean {
    return this.isSupervisor && issue.IsUpdated === true;
  }

  getNotes(issue: Issue) {
    this.selectedIssueId = issue.IssueId;
    this.selectedConversationId = issue.ConversationId || '';
    this.selectedPFLMIdChatHistory = issue.PFLMId || 0;
    this.resetChatAndAudit();
    // Build minimal payload for Project B
    const payload = {
      AssociateId: issue.AssociateId.toString(),
      PFLMId: issue.PFLMId,
      IsGenAIRO: this.isRiskOfficer
    };
    const userId = this.reviewerID.toString();
    this.loaderService.show();

    this.apiService.getNotesDataAPI(payload, userId).subscribe({
      next: (response: any) => {
        this.loaderService.hide();
        this.apiService.openNotes({
          open: true,
          data: response
        });
      },
      error: (e: any) => {
        this.loaderService.hide();
        console.error('Error fetching notes:', e);
        this.toasterService.setError({
          message: 'Unable to fetch notes data.',
          type: 'error',
        });
      }
    });
  }

  private resetChatAndAudit(): void {
    this.showChatHistory = false;
    this.showAuditLog = false;

    this.auditLogPflmId = 0;

    // Also notify services so any subscribed components close their windows
    this.apiService.showChatHistory$.next(false);
    this.apiService.showAuditLog$.next(false);
    this.apiService.chatHistoryIssueIds$.next('');
    this.apiService.auditLogIssueIds$.next('');
    this.apiService.auditLogPflmId$.next(0);
  }

  checkDataOrNot(): boolean {
    return this.issues && Object.keys(this.issues).length > 0;
  }
  // New methods
  /** Filtering methods */
  filterIssuesByFrequecy(frequency: string, type = 'not'): any {
    let filteredData = this.originalData.filter(issue => issue.Frequency === frequency);
    if (type !== 'default' && this.currentProcess) {
      filteredData = filteredData.filter(issue => +issue.ProcessId === +this.currentProcess);
    } this.userIssues = filteredData; // Apply date filter independently 
    if (this.currentDate) {
      const startDate = this.datePipe.transform(this.currentDate[0], 'MMddyyyy') || 0;
      const endDate = this.datePipe.transform(this.currentDate[1], 'MMddyyyy') || 0;
      filteredData = filteredData.filter(issue => {
        const issueDate = this.datePipe.transform(issue.CreatedDate, 'MMddyyyy') || 0;
        return +issueDate >= +startDate && +issueDate <= +endDate;
      });
    } this.issues = this.formatData(filteredData);
  }

  private filterIssuesByProject(frequency: string): any {
    let filteredData = this.originalData.filter(issue => issue.Frequency === frequency && +issue.ProjId === +this.currentProject);
    this.userIssues = filteredData; if (this.currentDate) {
      const startDate = this.datePipe.transform(this.currentDate[0], 'MMddyyyy') || 0;
      const endDate = this.datePipe.transform(this.currentDate[1], 'MMddyyyy') || 0;
      filteredData = filteredData.filter(issue => {
        const issueDate = this.datePipe.transform(issue.CreatedDate, 'MMddyyyy') || 0;
        return +issueDate >= +startDate && +issueDate <= +endDate;
      });
    } this.issues = this.formatData(filteredData);
  }
  /** Dropdown handlers */
  onClickSwitchProject(projectId: any): void {
    this.clearFiltersOnProjectChange();
    if (+projectId === 0) {
      this.filterIssuesByFrequecy('Daily', 'default');
    } else {
      // FIX: filter processes by ProjId, not ProcessId 
      this.processList = this.originalProcessList.filter(p => +p.ProjId === +projectId); // Apply project filter to issues 
      this.filterIssuesByProject('Daily');
    }
  }
  onProcessChange(processId: any): void {
    this.currentUser = 0;
    this.associatedList = [];
    if (+processId === 0) {
      this.filterIssuesByProject('Daily');
    } else {
      this.filterIssuesByFrequecy('Daily');
      this.getUserList(+processId);
    }
  }
  onUserChange(associateId: any): void {
    let filteredData = this.userIssues || [];
    if (+associateId !== 0) {
      filteredData = filteredData.filter(issue => +issue.AssociateId === +this.currentUser);
    }
    if (this.currentDate) {
      const startDate = this.datePipe.transform(this.currentDate[0], 'MMddyyyy') || 0;
      const endDate = this.datePipe.transform(this.currentDate[1], 'MMddyyyy') || 0;
      filteredData = filteredData.filter(issue => {
        const issueDate = this.datePipe.transform(issue.CreatedDate, 'MMddyyyy') || 0;
        return +issueDate >= +startDate && +issueDate <= +endDate;
      });
    } this.issues = this.formatData(filteredData);
  }

  onDateRangeChange(rawDate: any): void {
    if (rawDate) {
      const startDate = this.datePipe.transform(rawDate[0], 'MMddyyyy') || 0;
      const endDate = this.datePipe.transform(rawDate[1], 'MMddyyyy') || 0;
      if (this.userIssues?.length) {
        const filterData = this.userIssues.filter(issue => {
          const issueDate = this.datePipe.transform(issue.CreatedDate, 'MMddyyyy') || 0;
          return +issueDate >= +startDate && +issueDate <= +endDate;
        }); this.issues = this.formatData(filterData);
      }
    }
  }
  /** Clear filters */
  clearDates() {
    this.currentDate = null; this.filterIssuesByFrequecy('Daily', 'default');
  }
  onClearProject() {
    this.currentProject = 0;
    this.clearFiltersOnProjectChange();
    this.filterIssuesByFrequecy('Daily', 'default');
  }
  onClearProcess() {
    this.currentProcess = 0;
    this.currentUser = 0;
    this.associatedList = [];
    this.filterIssuesByProject('Daily');
  }
  onClearUser() {
    this.currentUser = 0; this.onUserChange(0);
  }
  clearFiltersOnProjectChange(): void {
    this.issues = [];
    this.processList = [];
    this.currentProcess = 0;
    this.associatedList = [];
    this.currentUser = 0;
    this.userIssues = [];
  }

  formatData(data: any): any {
    // 1. Group by AssociateId (First Layer)
    let groupById = data.reduce((temp: any, item: any) => {
      if (!temp[item.AssociateId]) temp[item.AssociateId] = [];
      temp[item.AssociateId].push(item);
      return temp;
    }, {});

    // 2. Group within Associate by Date AND PFLMId
    const finalArr = Object.keys(groupById).map(associateId => {
      const allIssuesForAssociate = groupById[associateId];

      const mergedGroups = allIssuesForAssociate.reduce((acc: any, current: any) => {
        // Create a composite key to handle the specific requirement
        const compositeKey = `${current.ConversationDate}_${current.PFLMId}`;

        if (!acc[compositeKey]) {
          acc[compositeKey] = { ...current };
        } else {
          // Same Date and Same PFLMId: Aggregate logic
          // Keep the record with the most recent CreatedDate for display details
          if (new Date(current.CreatedDate) > new Date(acc[compositeKey].CreatedDate)) {
            acc[compositeKey] = { ...current };
          }
        }
        return acc;
      }, {});

      // Convert back to array and sort by Date Descending
      return Object.values(mergedGroups).sort((a: any, b: any) =>
        new Date(b.ConversationDate).getTime() - new Date(a.ConversationDate).getTime()
      );
    });

    return finalArr;
  }

  getUserList(processId: number = 0): void {
    let uniqueUsers: any = [];
    this.originalAssociatedList.filter((item: any) => {
      if (!uniqueUsers[+item.AssociateId] && +item.ProcessId === processId) {
        uniqueUsers[+item.AssociateId] = item;
      }
      return true;
    });
    this.associatedList = Object.values(uniqueUsers);
  }

  getSortBy(list: any[], key: string, order: 'asc' | 'desc') {
    return list.sort((a, b) => order === 'asc' ? a[key].localeCompare(b[key]) : b[key].localeCompare(a[key]));
  }

  getAssociateName(data: any) {
    return data[0]?.AssociateName ?? 'Test';
  }

  getAssociateName2(issue: any) {
    return issue?.AssociateName ?? 'Test';
  }

  getProcessName(issue: any) {
    // FIX: Use originalProcessList if processList is empty
    const processList = !this.processList?.length ? this.originalProcessList : this.processList;
    return processList.find(p => p.ProcessId === issue.ProcessId)?.ProcessLocationName ?? '';
  }

  getLocalDate(issue: any) {
    const dateToDisplay = issue.ConversationDate || issue.CreatedDate;
    return this.datePipe.transform(dateToDisplay, 'MM/dd/yyyy');
  }

  getdetails(item: any) {
    return item?.value ?? [];
  }

  getAllStatusWithCount(issue: Issue) {
    const relatedIssues = this.originalData.filter(x =>
      x.AssociateId === issue.AssociateId &&
      // x.ConversationDate === issue.ConversationDate &&
      x.PFLMId === issue.PFLMId // Added this constraint
    );

    const openCount = relatedIssues.filter(x => x.IssueStatus?.toLowerCase() === 'open').length;
    const wipCount = relatedIssues.filter(x =>
      ['wip', 'in progress', 'inprogress'].includes(x.IssueStatus?.toLowerCase())
    ).length;

    const statusCount: any[] = [];
    if (openCount > 0) statusCount.push({ open: openCount });
    if (wipCount > 0) statusCount.push({ wip: wipCount });
    return statusCount;
  }

  onClickHeader(index: number, radio: HTMLInputElement) {
    radio.checked = true;
    this.onClickUser(index);
  }

  get isSupervisor(): boolean {
    const currentRole = (this.role || '').toLowerCase();
    return currentRole === 'supervisor' || currentRole === 'supervisors';
  }

  get isRiskOfficer(): boolean {
    const currentRole = (this.role || '').toLowerCase();
    return currentRole === 'riskofficer' || currentRole === 'riskofficers';
  }

}








